import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches'
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { debouncePromise } from '../../../js/utilities/functions/debounce.js'

const DEBOUNCE_MS = 200
const STALL_THRESHOLD_MS = 500
const QUERY_MINIMUM_LENGTH = 2
const POLLY_NO_RESULTS_MINIMUM_LENGTH = 3
const POLLY_NO_RESULTS_DELAY_MS = 1000
const POLLY_AVAILABLE_SELECTOR = '.ysa_hided_popup_div.visible'
const DESKTOP_MEDIA_QUERY = '(min-width: 992px)'

const getLocalizedUrl = (url, locale) => {
  return locale === 'fr_BE' ? `/fr${url}` : url
}

const renderProduct = ({ item, components, html, locale }) => html`
  <a href="${getLocalizedUrl(item.url, locale)}" class="aa-ItemLink text-decoration-none">
    <div class="aa-ItemContent d-flex align-items-center gap-2 gap-lg-3">
      <div class="aa-ItemIcon aa-ItemIcon--picture algolia-product-image flex-shrink-0">
        <img src="${item.image}" alt="${item.title}" width="80" height="80" class="img-fluid object-fit-contain" />
      </div>

      <div class="aa-ItemContentBody overflow-hidden">
        <div class="aa-ItemContentTitle algolia-product-title fw-semibold text-body">
          ${components.Highlight({
            hit: item,
            attribute: 'title',
          })}
        </div>

        <div class="aa-ItemContentDescription small text-body-secondary text-truncate">
          ${components.Snippet({
            hit: item,
            attribute: 'description',
          })}
        </div>
      </div>
    </div>
  </a>
`

const renderDocument = ({ item, components, html, locale }) => html`
  <a href="${getLocalizedUrl(item.url, locale)}" class="aa-ItemLink text-decoration-none">
    <div class="aa-ItemContent d-flex align-items-center gap-3">
      <div class="aa-ItemIcon aa-ItemIcon--picture algolia-document-image flex-shrink-0">
        <img src="${item.image}" alt="${item.content_title}" width="80" height="80" class="img-fluid object-fit-contain" />
      </div>

      <div class="aa-ItemContentBody overflow-hidden">
        <div class="aa-ItemContentTitle fw-semibold text-body text-truncate">
          ${components.Highlight({
            hit: item,
            attribute: 'content_title',
          })}
        </div>

        <div class="aa-ItemContentDescription small text-body-secondary text-truncate">
          ${components.Snippet({
            hit: item,
            attribute: 'content',
          })}
        </div>
      </div>
    </div>
  </a>
`

const renderSourceHeader = ({ html, title }) => html`
  <div class="d-flex align-items-center gap-2 px-2 pt-2">
    <span class="small fw-semibold text-body">${title}</span>
    <hr class="flex-grow-1 my-0" />
  </div>
`

const getRecentSearches = ({ items, limit }) => {
  return items
    .slice(0, limit)
    .map((item) => {
      /*
       * Oudere opgeslagen items kunnen nog `query` gebruiken.
       * De huidige Recent Searches-plugin gebruikt `label`.
       */
      const label = item.label ?? item.query

      return {
        ...item,
        id: item.id ?? label,
        label,
        _highlightResult: {
          label: {
            value: label,
          },
        },
      }
    })
    .filter((item) => item.label)
}

const initializeAlgoliaAutocomplete = () => {
  const containers = [...document.querySelectorAll('.js-algolia-autocomplete')]

  const applicationConfig = window.polyestershoppen

  if (containers.length === 0 || !applicationConfig?.algolia) {
    return
  }

  const { appId, apiKey, indexes } = applicationConfig.algolia
  const { locale, webshop } = applicationConfig.config

  const searchClient = algoliasearch(appId, apiKey)
  const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY)

  /*
   * Zoek de containers op basis van hun Bootstrap-wrappers.
   * De fallback op de arrayvolgorde maakt het ook bruikbaar wanneer
   * later maar één zoekcontainer op een pagina aanwezig is.
   */
  const desktopContainer =
    containers.find((container) => {
      return container.closest('.d-none.d-lg-block')
    }) ?? containers[0]

  const mobileContainer =
    containers.find((container) => {
      return container.closest('.d-lg-none')
    }) ??
    containers[1] ??
    containers[0]

  const mobileCollapse = mobileContainer.closest('.collapse')

  let activeContainer = null
  let activeInstance = null
  let currentQuery = ''
  let isPanelOpen = false
  let pollyNoResultsTimeout = null
  let pollyMessageSent = false

  /*
   * De backdrop wordt via SCSS alleen op mobiele schermen getoond.
   */
  const searchBackdrop = document.createElement('div')

  searchBackdrop.className = 'algolia-search-backdrop'
  searchBackdrop.setAttribute('aria-hidden', 'true')

  document.body.append(searchBackdrop)

  const updateSearchBackdrop = () => {
    searchBackdrop.classList.toggle('show', isPanelOpen)
  }

  const closeActiveAutocompletePanel = () => {
    window.clearTimeout(pollyNoResultsTimeout)
    activeInstance?.setIsOpen(false)
    activeContainer?.querySelector('.aa-Input')?.blur()

    isPanelOpen = false
    updateSearchBackdrop()
  }

  const schedulePollyForNoResults = (state, container, messageTemplate) => {
    window.clearTimeout(pollyNoResultsTimeout)

    const query = state.query.trim()

    if (!query) {
      pollyMessageSent = false
    }

    if (
      pollyMessageSent ||
      !messageTemplate ||
      state.status !== 'idle' ||
      query.length < POLLY_NO_RESULTS_MINIMUM_LENGTH ||
      container.querySelector('.aa-Input') !== document.activeElement
    ) {
      return
    }

    // Zoeksuggesties en recente zoekopdrachten tellen niet als resultaten.
    const resultCollections = state.collections.filter(({ source }) => source.sourceId === 'products' || source.sourceId === 'documents')

    if (resultCollections.length !== 2 || resultCollections.some(({ items }) => items.length > 0)) {
      return
    }

    // Geef de bezoeker de tijd om de zoekopdracht af te maken.
    pollyNoResultsTimeout = window.setTimeout(() => {
      if (
        pollyMessageSent ||
        container !== activeContainer ||
        currentQuery.trim() !== query ||
        container.querySelector('.aa-Input') !== document.activeElement ||
        typeof window.ysa_sendMessage !== 'function' ||
        !(document.querySelector(POLLY_AVAILABLE_SELECTOR) instanceof HTMLElement)
      ) {
        return
      }

      window.ysa_sendMessage(messageTemplate.replace('{query}', query))
      pollyMessageSent = true

      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: 'polly_search_no_results',
        search_term: query,
        search_source: 'algolia_autocomplete',
      })
    }, POLLY_NO_RESULTS_DELAY_MS)
  }

  const createAutocompleteInstance = (container) => {
    const { placeholder, cancelButton, productsTitle, documentsTitle, labelAllResults, searchUrl, pollyNoResultsMessage } = container.dataset

    /*
     * De debounce hoort bij de actieve instance.
     * Bij een viewportwisseling krijgt de nieuwe instance een eigen timer.
     */
    const debouncedSources = debouncePromise((sources) => Promise.resolve(sources), DEBOUNCE_MS)

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient,
      indexName: indexes.productSuggestions,

      getSearchParams: () => ({
        hitsPerPage: 2,
        clickAnalytics: true,
      }),

      transformSource: ({ source }) => ({
        ...source,

        onSelect: ({ setIsOpen }) => {
          setIsOpen(true)
        },
      }),
    })

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'RECENT_SEARCH',
      limit: 2,

      /*
       * Toon altijd de laatste twee zoekopdrachten.
       * Filter deze niet op de huidige query.
       */
      search: getRecentSearches,

      transformSource: ({ source }) => ({
        ...source,

        onSelect: ({ setIsOpen }) => {
          setIsOpen(true)
        },
      }),
    })

    return autocomplete({
      container,

      /*
       * Behoud de ingevulde zoekterm bij een viewportwisseling.
       */
      initialState: {
        query: currentQuery,
      },

      placeholder,
      stallThreshold: STALL_THRESHOLD_MS,
      openOnFocus: true,
      insights: true,

      /*
       * De mobiele zoekbalk gebruikt al een Bootstrap-collapse.
       */
      detachedMediaQuery: 'none',

      panelPlacement: 'input-wrapper-width',

      classNames: {
        root: 'w-100 algolia-autocomplete',
        form: 'bg-body border-0 rounded-pill shadow-none',
        input: 'text-body',
        panel: 'algolia-autocomplete-panel bg-body border rounded-3 shadow-lg',
        sourceHeader: 'border-0',
        sourceFooter: 'border-0',
      },

      translations: {
        detachedCancelButtonText: cancelButton,
        clearButtonTitle: cancelButton,
      },

      plugins: [querySuggestionsPlugin, recentSearchesPlugin],

      reshape: ({ sourcesBySourceId }) => {
        const { recentSearchesPlugin, querySuggestionsPlugin, products, documents, ...otherSources } = sourcesBySourceId

        return [recentSearchesPlugin, querySuggestionsPlugin, products, documents, ...Object.values(otherSources)].filter(Boolean)
      },

      onStateChange: ({ state }) => {
        /*
         * Negeer een eventuele late state-update van een instance
         * die tijdens een viewportwisseling al is vernietigd.
         */
        if (container !== activeContainer) {
          return
        }

        currentQuery = state.query
        isPanelOpen = state.isOpen

        updateSearchBackdrop()
        schedulePollyForNoResults(state, container, pollyNoResultsMessage)
      },

      onReset: ({ setIsOpen }) => {
        window.requestAnimationFrame(() => {
          setIsOpen(false)
          container.querySelector('.aa-Input')?.blur()
        })
      },

      onSubmit: ({ state }) => {
        const destination = new URL(searchUrl, window.location.origin)

        destination.searchParams.set('phrase', state.query)

        window.location.assign(destination.toString())
      },

      getSources: ({ query }) => {
        return debouncedSources([
          {
            sourceId: 'products',

            getItems: () => {
              if (query.length < QUERY_MINIMUM_LENGTH) {
                return []
              }

              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexes.product,
                    params: {
                      query,
                      hitsPerPage: 6,
                      clickAnalytics: true,
                      filters: `is_link: false AND ` + `(is_webshop_restricted: false OR webshops: ${webshop})`,
                    },
                  },
                ],
              })
            },

            getItemUrl: ({ item }) => {
              return getLocalizedUrl(item.url, locale)
            },

            onSelect: ({ item }) => {
              if (!item.__autocomplete_queryID) {
                return
              }

              window.localStorage.setItem('algoliasearch-queryID', item.__autocomplete_queryID)
            },

            templates: {
              header: ({ html }) => {
                return renderSourceHeader({
                  html,
                  title: productsTitle,
                })
              },

              item: ({ item, components, html }) => {
                return renderProduct({
                  item,
                  components,
                  html,
                  locale,
                })
              },

              footer: ({ html }) => {
                if (!query.length) {
                  return null
                }

                const destination = new URL(searchUrl, window.location.origin)

                destination.searchParams.set('phrase', query)

                return html`
                  <div class="algolia-results-footer border-top">
                    <a href="${destination.toString()}" class="btn btn-outline-info w-100">
                      ${labelAllResults}
                      <i class="ci-arrow-right ms-2"></i>
                    </a>
                  </div>
                `
              },
            },
          },

          {
            sourceId: 'documents',

            getItems: () => {
              if (query.length < QUERY_MINIMUM_LENGTH) {
                return []
              }

              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexes.document,
                    params: {
                      query,
                      hitsPerPage: 4,
                      clickAnalytics: true,
                    },
                  },
                ],
              })
            },

            getItemUrl: ({ item }) => {
              return getLocalizedUrl(item.url, locale)
            },

            templates: {
              header: ({ html }) => {
                return renderSourceHeader({
                  html,
                  title: documentsTitle,
                })
              },

              item: ({ item, components, html }) => {
                return renderDocument({
                  item,
                  components,
                  html,
                  locale,
                })
              },
            },
          },
        ]).catch((error) => {
          // Tijdens snel typen annuleert debouncePromise oudere zoekopdrachten.
          // Die annuleringen mogen geen unhandledrejection veroorzaken.
          if (error?._cancelled) {
            return []
          }

          throw error
        })
      },
    })
  }

  const getContainerForCurrentViewport = () => {
    return desktopMediaQuery.matches ? desktopContainer : mobileContainer
  }

  const mountAutocompleteForCurrentViewport = () => {
    const nextContainer = getContainerForCurrentViewport()

    if (!nextContainer || (nextContainer === activeContainer && activeInstance)) {
      return
    }

    /*
     * Sluit en verwijder eerst de huidige instance.
     */
    window.clearTimeout(pollyNoResultsTimeout)
    activeContainer?.querySelector('.aa-Input')?.blur()
    activeInstance?.destroy()

    if (activeContainer) {
      delete activeContainer.algoliaAutocomplete
    }

    isPanelOpen = false
    updateSearchBackdrop()

    /*
     * Stel activeContainer in vóór autocomplete() wordt aangeroepen,
     * zodat onStateChange direct de juiste container herkent.
     */
    activeContainer = nextContainer
    activeInstance = createAutocompleteInstance(nextContainer)

    nextContainer.algoliaAutocomplete = activeInstance

    /*
     * Wanneer de mobiele collapse bij een viewportwisseling al open
     * staat, focus dan de nieuw opgebouwde input.
     */
    if (nextContainer === mobileContainer && mobileCollapse?.classList.contains('show')) {
      window.requestAnimationFrame(() => {
        nextContainer.querySelector('.aa-Input')?.focus()
      })
    }
  }

  searchBackdrop.addEventListener('click', (event) => {
    event.stopPropagation()
    closeActiveAutocompletePanel()
  })

  /*
   * Registreer de Bootstrap-collapse-events maar één keer.
   */
  if (mobileCollapse) {
    mobileCollapse.addEventListener('shown.bs.collapse', () => {
      if (activeContainer !== mobileContainer) {
        return
      }

      mobileContainer.querySelector('.aa-Input')?.focus()
    })

    mobileCollapse.addEventListener('hide.bs.collapse', () => {
      if (activeContainer !== mobileContainer) {
        return
      }

      closeActiveAutocompletePanel()
    })
  }

  document.addEventListener('click', (event) => {
    const target = event.target

    if (!(target instanceof Element)) {
      return
    }

    if (target.closest('.aa-Autocomplete, ' + '.aa-Panel, ' + '.algolia-autocomplete, ' + '.algolia-autocomplete-panel')) {
      return
    }

    closeActiveAutocompletePanel()
  })

  document.addEventListener('keydown', (event) => {
    if (!event.altKey || event.code !== 'KeyF') {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    /*
     * Open op mobiel eerst de Bootstrap-collapse.
     * Het shown-event focust daarna de Algolia-input.
     */
    if (activeContainer === mobileContainer && mobileCollapse && !mobileCollapse.classList.contains('show')) {
      const collapseToggle = mobileCollapse.id ? document.querySelector(`[data-bs-target="#${mobileCollapse.id}"]`) : null

      collapseToggle?.click()

      return
    }

    activeContainer?.querySelector('.aa-Input')?.focus()
  })

  /*
   * Bouw bij het laden de juiste instance op en wissel deze wanneer
   * het Bootstrap lg-breakpoint wordt gepasseerd.
   */
  mountAutocompleteForCurrentViewport()

  desktopMediaQuery.addEventListener('change', mountAutocompleteForCurrentViewport)
}

initializeAlgoliaAutocomplete()
